import { useCallback } from 'react';

import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useCreateRequestEventMutation } from '@/app/services/issues';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  RequestEventFragment,
  RequestEventType,
  RequestFragment,
} from '@/rtk-query.gql';

import { ICONS } from './constants';
import { useRequestState } from './useRequestState';

type ApproveOrDenyProps = {
  request: RequestFragment;
};

export const ApproveOrDeny = ({ request }: ApproveOrDenyProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // const { me } = useAppSelector((state) => state.authentication);

  const [createEvent, { isLoading }] = useCreateRequestEventMutation();

  const currentState = useRequestState(
    request as RequestFragment & { events: RequestEventFragment[] },
  );

  const isApproved = currentState === RequestEventType.APPROVED;
  const isDenied = currentState === RequestEventType.DENIED;

  //TODO ask Gary about below before removing this
  // const isInProgress = currentState === RequestEventType.IN_PROGRESS;
  // const isComplete = currentState === RequestEventType.COMPLETED;
  // const requestType = request.type;

  const approve = useCallback(async () => {
    try {
      await createEvent({
        content: '',
        requestId: request.id,
        type: RequestEventType.APPROVED,
        refRequestId: null,
      });
      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail: t('issues.approvedIssue'),
          life: ToastLife.DEFAULT,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('issues.errorApprovingIssue') + error,
          life: ToastLife.DEFAULT,
        }),
      );
    }
  }, [createEvent, dispatch, request.id, t]);

  const deny = useCallback(async () => {
    try {
      await createEvent({
        content: '',
        requestId: request.id,
        type: RequestEventType.DENIED,
        refRequestId: null,
      });
      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail: t('issues.deniedIssue'),
          life: ToastLife.DEFAULT,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('issues.errorDenyingIssue') + error,
          life: ToastLife.DEFAULT,
        }),
      );
    }
  }, [createEvent, dispatch, request.id, t]);

  //TODO ask Gary about this
  // if (
  //   !me ||
  //   request.issue?.__typename !== 'RequestItIssue' ||
  //   me.adUserId !== request.issue?.sponsorId ||
  //   isInProgress ||
  //   isComplete ||
  //   isDenied
  // ) {
  //   return null;
  // }

  return (
    <div className="p-buttonset">
      <Button
        label={t('common.approve')}
        className="status-button status-button--approve"
        icon={ICONS[RequestEventType.APPROVED]}
        loading={isLoading}
        disabled={isLoading || isApproved}
        onClick={approve}
      />
      <Button
        label={t('common.deny')}
        className="status-button status-button--deny"
        icon={ICONS[RequestEventType.DENIED]}
        loading={isLoading}
        disabled={isLoading || isDenied}
        onClick={deny}
        severity="secondary"
      />
    </div>
  );
};
